import { GlobalContext } from "@/Context/GlobalProvider";
import { isMobile } from "@/helpers/user";
import useDownload from "@/Hooks/useDownload";
import PropertyCardMini from "@/Pages/Properties/Items/PropertyCardMini";
import { Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import GetLinkButton from "./GetLinkButton";
import Spinner from "./Spinner";

export default function ForwardModal() {
    const { favorites, isOpenForwardModal, closeForwardModal } =
        useContext(GlobalContext);
    const [properties, setProperties] = useState([]);
    const [shareLink, setShareLink] = useState("");
    const [openSocialModal, setOpenSocialModal] = useState(false);
    const { isDownloading, download } = useDownload();

    const modalRef = useRef(null);

    function downloadAllProperties() {
        download(
            properties.reduce((acc, property) => {
                if (property.has_password) {
                    return acc;
                }

                return [
                    ...acc,
                    {
                        url: `/windowcard-url/${property.slug}`,
                        title: property.title,
                    },
                ];
            }, [])
        );
    }

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                closeForwardModal();
            }
        };

        const handleClickOutside = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                closeForwardModal();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const favoriteParams = favorites.join(",");
        setShareLink(
            route("property.collection") + `?references=${favoriteParams}`
        );

        fetch(`/api/favorites?properties=${favoriteParams}`)
            .then((response) => response.json())
            .then((data) => {
                setProperties(data.favoriteList);

                if (data.favoriteList.length === 0) {
                    closeForwardModal();
                }
            })
            .catch((error) => {
                console.error("Error fetching favorites:", error);
            });
    }, [favorites]);

    return (
        <Transition
            show={isOpenForwardModal}
            as={Fragment}
            enter="transition-all ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-all ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div className="fixed top-0 left-0 w-full h-full bg-light/60  z-30 text-light flex flex-col justify-center">
                <Transition.Child
                    as={Fragment}
                    enter="transition-all ease-out duration-200"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition-all ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <div
                        ref={modalRef}
                        className="max-w-4xl mx-auto bg-white max-h-full overflow-y-auto w-content py-4 px-4 sm:px-8 relative rounded-md"
                    >
                        <h2 className="text-2xl font-bold sm:text-3xl text-center mt-10 sm:mt-4">
                            Forward Selection
                        </h2>
                        <button
                            onClick={() => {
                                closeForwardModal();
                            }}
                            className="absolute right-5 sm:right-8 top-5 sm:top-8"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="32"
                                viewBox="0 -960 960 960"
                                width="32"
                                fill="currentColor"
                            >
                                <path d="M256-227.69 227.69-256l224-224-224-224L256-732.31l224 224 224-224L732.31-704l-224 224 224 224L704-227.69l-224-224-224 224Z" />
                            </svg>
                        </button>
                        <div className="mx-auto w-content my-8">
                            <div className="hidden max-w-2xl mx-auto md:flex gap-8 justify-between flex-wrap font-serif text-lg">
                                <a
                                    href={`https://api.whatsapp.com/send?text=${shareLink}`}
                                    className="flex items-center gap-1.5 hover:bg-light hover:text-white transition-colors duration-300 py-1.5 px-2.5 rounded-md"
                                    target="_blank"
                                >
                                    <svg
                                        className="size-5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 448 512"
                                    >
                                        <path
                                            fill="currentColor"
                                            d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7 .9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"
                                        />
                                    </svg>
                                    <span>WhatsApp</span>
                                </a>
                                <a
                                    href={`mailto:?body=${shareLink}`}
                                    className="flex items-center gap-1.5 hover:bg-light hover:text-white transition-colors duration-300 py-1.5 px-2.5 rounded-md"
                                    target="_blank"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="24"
                                        viewBox="0 -960 960 960"
                                        width="24"
                                        fill="currentColor"
                                    >
                                        <path d="M172.31-180Q142-180 121-201q-21-21-21-51.31v-455.38Q100-738 121-759q21-21 51.31-21h615.38Q818-780 839-759q21 21 21 51.31v455.38Q860-222 839-201q-21 21-51.31 21H172.31ZM800-662.31 499.46-469.92q-4.61 2.61-9.54 4.11-4.92 1.5-9.92 1.5t-9.92-1.5q-4.93-1.5-9.54-4.11L160-662.31v410q0 5.39 3.46 8.85t8.85 3.46h615.38q5.39 0 8.85-3.46t3.46-8.85v-410ZM480-520l313.85-200h-627.7L480-520ZM160-662.31v9.23-45.73 1.19V-720v22.38-1.27V-653.08v-9.23V-240v-422.31Z" />
                                    </svg>
                                    <span>Mail</span>
                                </a>
                                <button
                                    onClick={() => downloadAllProperties()}
                                    className="flex items-center gap-2 hover:bg-light hover:text-white transition-colors duration-300 py-1.5 px-2.5 rounded-md"
                                >
                                    {(!isDownloading && (
                                        <svg
                                            className="size-5"
                                            version="1.1"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="253 265 49.5 49.5"
                                        >
                                            <g>
                                                <path
                                                    fill="currentColor"
                                                    d="M278.88,267.33c0-0.66-0.54-1.2-1.2-1.2s-1.2,0.54-1.2,1.2v32.6c0,0.66,0.54,1.2,1.2,1.2s1.2-0.54,1.2-1.2
                                        V267.33z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M287.19,290.41c-0.23-0.23-0.53-0.35-0.85-0.35s-0.62,0.13-0.85,0.35l-7.28,7.28l-0.54,0.54l-0.54-0.54
                                        l-7.28-7.28c-0.23-0.23-0.53-0.35-0.85-0.35s-0.62,0.13-0.85,0.35c-0.47,0.47-0.47,1.23,0,1.7l8.66,8.66
                                        c0.23,0.23,0.53,0.35,0.85,0.35s0.62-0.13,0.85-0.35l8.66-8.66C287.66,291.64,287.66,290.88,287.19,290.41z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M302.14,299.93c0-0.66-0.54-1.2-1.2-1.2c-0.66,0-1.2,0.54-1.2,1.2v7.6c0,2.57-2.09,4.67-4.67,4.67h-34.54
                                        c-2.57,0-4.67-2.09-4.67-4.67v-7.6c0-0.66-0.54-1.2-1.2-1.2s-1.2,0.54-1.2,1.2v7.6c0,3.9,3.17,7.07,7.07,7.07h34.54
                                        c3.9,0,7.07-3.17,7.07-7.07V299.93z"
                                                />
                                            </g>
                                        </svg>
                                    )) || (
                                        <Spinner
                                            size="size-5"
                                            display="inline"
                                        />
                                    )}
                                    <span>Download WC</span>
                                </button>
                                <GetLinkButton url={shareLink} />
                            </div>
                            <div className="flex justify-around flex-wrap gap-x-4 gap-y-2 md:hidden">
                                <div className="relative">
                                    <button
                                        className="flex items-center gap-2 "
                                        onClick={() => {
                                            if (isMobile()) {
                                                navigator.share({
                                                    url: shareLink,
                                                });

                                                return;
                                            }
                                            setOpenSocialModal(
                                                !openSocialModal
                                            );
                                        }}
                                    >
                                        <svg
                                            className="size-[18px]"
                                            version="1.1"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="255 265 50 50"
                                        >
                                            <g>
                                                <path
                                                    fill="currentColor"
                                                    d="M265.72,282.08c-4.56,0-8.28,3.71-8.28,8.28c0,4.56,3.71,8.28,8.28,8.28s8.28-3.71,8.28-8.28
                                    C273.99,285.79,270.28,282.08,265.72,282.08z M265.72,296.23c-3.24,0-5.87-2.63-5.87-5.87s2.63-5.87,5.87-5.87
                                    c3.24,0,5.87,2.63,5.87,5.87S268.95,296.23,265.72,296.23z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M289.88,266.32c-4.56,0-8.28,3.71-8.28,8.28s3.71,8.28,8.28,8.28c4.56,0,8.28-3.71,8.28-8.28
                                    S294.44,266.32,289.88,266.32z M289.88,280.47c-3.24,0-5.87-2.63-5.87-5.87s2.63-5.87,5.87-5.87c3.24,0,5.87,2.63,5.87,5.87
                                    S293.11,280.47,289.88,280.47z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M289.88,297.85c-4.56,0-8.28,3.71-8.28,8.28s3.71,8.28,8.28,8.28c4.56,0,8.28-3.71,8.28-8.28
                                    S294.44,297.85,289.88,297.85z M289.88,312c-3.24,0-5.87-2.63-5.87-5.87s2.63-5.87,5.87-5.87c3.24,0,5.87,2.63,5.87,5.87
                                    S293.11,312,289.88,312z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M285.14,278.78c0.06-0.32-0.01-0.64-0.19-0.9c-0.22-0.33-0.6-0.52-0.99-0.52c-0.24,0-0.48,0.07-0.68,0.21
                                    l-11.25,7.87c-0.55,0.38-0.68,1.13-0.31,1.67c0.22,0.33,0.6,0.52,0.99,0.52c0.24,0,0.48-0.07,0.68-0.21l11.25-7.87
                                    C284.9,279.37,285.08,279.1,285.14,278.78z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M285.14,301.98c0.06,0.32-0.01,0.64-0.19,0.9c-0.22,0.33-0.6,0.52-0.99,0.52c-0.24,0-0.48-0.07-0.68-0.21
                                    l-11.25-7.87c-0.55-0.38-0.68-1.13-0.31-1.67c0.22-0.33,0.6-0.52,0.99-0.52c0.24,0,0.48,0.07,0.68,0.21l11.25,7.87
                                    C284.9,301.39,285.08,301.67,285.14,301.98z"
                                                />
                                            </g>
                                        </svg>
                                        <span className="uppercase font-serif mt-1">
                                            share
                                        </span>
                                    </button>
                                    {openSocialModal && (
                                        <div className="absolute top-8 left-0 space-y-3 w-max bg-white px-4 py-3 border border-light">
                                            <a
                                                href={`https://api.whatsapp.com/send?text=${shareLink}`}
                                                className="flex items-center gap-2"
                                                target="_blank"
                                            >
                                                <svg
                                                    className="size-5"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 448 512"
                                                >
                                                    <path
                                                        fill="currentColor"
                                                        d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7 .9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"
                                                    />
                                                </svg>
                                                <span>WhatsApp</span>
                                            </a>
                                            <a
                                                href={`mailto:?body=${shareLink}`}
                                                className="flex items-center gap-2"
                                                target="_blank"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    height="24"
                                                    viewBox="0 -960 960 960"
                                                    width="24"
                                                    fill="currentColor"
                                                >
                                                    <path d="M172.31-180Q142-180 121-201q-21-21-21-51.31v-455.38Q100-738 121-759q21-21 51.31-21h615.38Q818-780 839-759q21 21 21 51.31v455.38Q860-222 839-201q-21 21-51.31 21H172.31ZM800-662.31 499.46-469.92q-4.61 2.61-9.54 4.11-4.92 1.5-9.92 1.5t-9.92-1.5q-4.93-1.5-9.54-4.11L160-662.31v410q0 5.39 3.46 8.85t8.85 3.46h615.38q5.39 0 8.85-3.46t3.46-8.85v-410ZM480-520l313.85-200h-627.7L480-520ZM160-662.31v9.23-45.73 1.19V-720v22.38-1.27V-653.08v-9.23V-240v-422.31Z" />
                                                </svg>
                                                <span className="">Mail</span>
                                            </a>
                                            <a
                                                href={`https://www.linkedin.com/shareArticle?url=${shareLink}`}
                                                className="flex items-center gap-2"
                                                target="_blank"
                                            >
                                                <svg
                                                    className="size-5"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 448 512"
                                                >
                                                    <path
                                                        fill="currentColor"
                                                        d="M100.3 448H7.4V148.9h92.9zM53.8 108.1C24.1 108.1 0 83.5 0 53.8a53.8 53.8 0 0 1 107.6 0c0 29.7-24.1 54.3-53.8 54.3zM447.9 448h-92.7V302.4c0-34.7-.7-79.2-48.3-79.2-48.3 0-55.7 37.7-55.7 76.7V448h-92.8V148.9h89.1v40.8h1.3c12.4-23.5 42.7-48.3 87.9-48.3 94 0 111.3 61.9 111.3 142.3V448z"
                                                    />
                                                </svg>
                                                <span className=" mt-1">
                                                    LinkedIn
                                                </span>
                                            </a>
                                            <a
                                                href={`https://www.facebook.com/sharer/sharer.php?u=${shareLink}`}
                                                className="flex items-center gap-1"
                                                target="_blank"
                                            >
                                                <svg
                                                    className="size-5"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 320 512"
                                                >
                                                    <path
                                                        fill="currentColor"
                                                        d="M80 299.3V512H196V299.3h86.5l18-97.8H196V166.9c0-51.7 20.3-71.5 72.7-71.5c16.3 0 29.4 .4 37 1.2V7.9C291.4 4 256.4 0 236.2 0C129.3 0 80 50.5 80 159.4v42.1H14v97.8H80z"
                                                    />
                                                </svg>
                                                <span className="">
                                                    Facebook
                                                </span>
                                            </a>
                                        </div>
                                    )}
                                </div>
                                <GetLinkButton url={shareLink} />
                                <button
                                    onClick={() => downloadAllProperties()}
                                    className="flex items-center gap-2 uppercase font-serif hover:bg-light hover:text-white transition-colors duration-300 py-1.5 px-2.5 rounded-md"
                                >
                                    {(!isDownloading && (
                                        <svg
                                            className="size-5"
                                            version="1.1"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="253 265 49.5 49.5"
                                        >
                                            <g>
                                                <path
                                                    fill="currentColor"
                                                    d="M278.88,267.33c0-0.66-0.54-1.2-1.2-1.2s-1.2,0.54-1.2,1.2v32.6c0,0.66,0.54,1.2,1.2,1.2s1.2-0.54,1.2-1.2
                                        V267.33z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M287.19,290.41c-0.23-0.23-0.53-0.35-0.85-0.35s-0.62,0.13-0.85,0.35l-7.28,7.28l-0.54,0.54l-0.54-0.54
                                        l-7.28-7.28c-0.23-0.23-0.53-0.35-0.85-0.35s-0.62,0.13-0.85,0.35c-0.47,0.47-0.47,1.23,0,1.7l8.66,8.66
                                        c0.23,0.23,0.53,0.35,0.85,0.35s0.62-0.13,0.85-0.35l8.66-8.66C287.66,291.64,287.66,290.88,287.19,290.41z"
                                                />
                                                <path
                                                    fill="currentColor"
                                                    d="M302.14,299.93c0-0.66-0.54-1.2-1.2-1.2c-0.66,0-1.2,0.54-1.2,1.2v7.6c0,2.57-2.09,4.67-4.67,4.67h-34.54
                                        c-2.57,0-4.67-2.09-4.67-4.67v-7.6c0-0.66-0.54-1.2-1.2-1.2s-1.2,0.54-1.2,1.2v7.6c0,3.9,3.17,7.07,7.07,7.07h34.54
                                        c3.9,0,7.07-3.17,7.07-7.07V299.93z"
                                                />
                                            </g>
                                        </svg>
                                    )) || (
                                        <Spinner
                                            size="size-5"
                                            display="inline"
                                        />
                                    )}
                                    <span>Download WC</span>
                                </button>
                            </div>
                        </div>
                        <div className="scrollbar my-8 max-w-3xl mx-auto w-full space-y-8 max-h-[450px] overflow-y-auto pr-3 sm:pr-0">
                            {properties.map((property) => (
                                <PropertyCardMini
                                    key={property.id}
                                    property={property}
                                />
                            ))}
                        </div>
                    </div>
                </Transition.Child>
            </div>
        </Transition>
    );
}
